/**
 * lib/cards/layout-constraints.js — Editor constraint enforcement.
 *
 * Chunk 3 (Apr 24 2026). Encodes the rules from spec §5 plus the
 * interactive logic the editor needs (clamp while dragging, decide
 * which elements can be deleted, which types can be added).
 *
 * Called from:
 *   - EditorCanvas.jsx on drag/resize to clamp new coordinates
 *   - Inspector.jsx to determine which controls to show per element
 *   - CardEditorModal.jsx on save to run the pre-flight check
 *
 * Mirrors validateLayout() in render-layout.js (which handles post-
 * load validation). Drift-risk sibling of validateLayoutServer() in
 * api/src/routes/card-settings.js.
 */

import { CR80 } from './shared.js';

export const QR_MIN_MM = 18;
export const BLEED_MM = 2; // Soft constraint — warning not enforcement

// Types the user is allowed to delete from the layout. Everything
// else is "required" — editor hides the delete control.
export const DELETABLE_TYPES = new Set([
  'static_text',
  'static_rect',
  'static_divider',
  'static_image',
  'dynamic_role',         // role is optional (staff may not have one)
  'dynamic_logo',         // logo is optional (text fallback exists)
  'dynamic_banner',
  'dynamic_footer_text',
]);

// Types that MUST exist for the card to function. Editor restores
// them on save if somehow missing.
export const REQUIRED_TYPES = new Set([
  'dynamic_photo',
  'dynamic_name',
  'dynamic_qr',
]);

// Per-type minimum dimensions (mm). Everything else uses 3×3mm minimum
// to stay manipulable on small screens.
export const MIN_DIMENSIONS = {
  dynamic_qr:    { w: QR_MIN_MM, h: QR_MIN_MM },
  dynamic_photo: { w: 10, h: 10 },
  dynamic_logo:  { w: 8,  h: 4  },
};
const DEFAULT_MIN = { w: 3, h: 3 };

function minDims(type) {
  return MIN_DIMENSIONS[type] || DEFAULT_MIN;
}

/**
 * Clamp a proposed element bounds to the card and to minimum sizes.
 * Used live during drag + resize to prevent illegal states from ever
 * appearing on screen.
 */
export function clampElement(next, type) {
  const min = minDims(type);
  const card = { w: CR80.widthMm, h: CR80.heightMm };

  let { x, y, w, h } = next;

  // Enforce min size
  if (w < min.w) w = min.w;
  if (h < min.h) h = min.h;

  // Clamp within card bounds
  if (x < 0) x = 0;
  if (y < 0) y = 0;
  if (x + w > card.w) x = card.w - w;
  if (y + h > card.h) y = card.h - h;

  // Handle corner case where min size itself exceeds card (shouldn't
  // happen at CR80 + 18mm QR min, but defensive anyway)
  if (w > card.w) w = card.w;
  if (h > card.h) h = card.h;

  return { x, y, w, h };
}

/**
 * Should the delete control appear for this element?
 */
export function canDelete(element) {
  if (element.locked) return false;
  return DELETABLE_TYPES.has(element.type);
}

/**
 * Full layout pre-flight. Called on save. Returns { ok, errors, warnings }.
 * Errors block the save; warnings are shown but allow save.
 */
export function validateLayoutForSave(layout) {
  const errors = [];
  const warnings = [];

  if (!layout || typeof layout !== 'object') {
    return { ok: false, errors: ['layout is not an object'], warnings: [] };
  }
  if (layout.version !== 1) {
    errors.push(`unsupported layout version: ${layout.version}`);
  }
  if (!Array.isArray(layout.elements)) {
    errors.push('layout.elements is not an array');
    return { ok: false, errors, warnings };
  }

  const seen = new Set();
  const typesSeen = new Set();

  for (const el of layout.elements) {
    if (!el.id) {
      errors.push('element missing id');
      continue;
    }
    if (seen.has(el.id)) errors.push(`duplicate element id: ${el.id}`);
    seen.add(el.id);

    if (!el.type) errors.push(`${el.id}: missing type`);
    else typesSeen.add(el.type);

    if (typeof el.x !== 'number' || typeof el.y !== 'number'
        || typeof el.w !== 'number' || typeof el.h !== 'number') {
      errors.push(`${el.id}: x/y/w/h must be numbers`);
      continue;
    }
    if (el.w <= 0 || el.h <= 0) errors.push(`${el.id}: w/h must be > 0`);
    if (el.x < 0 || el.y < 0
        || el.x + el.w > CR80.widthMm + 0.01
        || el.y + el.h > CR80.heightMm + 0.01) {
      errors.push(`${el.id}: extends outside card bounds`);
    }
    if (el.type === 'dynamic_qr' && Math.min(el.w, el.h) < QR_MIN_MM - 0.01) {
      errors.push(`${el.id}: QR smaller than ${QR_MIN_MM}mm`);
    }

    // Soft: QR under 20mm = warning
    if (el.type === 'dynamic_qr' && Math.min(el.w, el.h) < 20 - 0.01) {
      warnings.push(`${el.id}: QR is smaller than 20mm — scan reliability may drop`);
    }
  }

  for (const req of REQUIRED_TYPES) {
    if (!typesSeen.has(req)) errors.push(`missing required element: ${req}`);
  }

  return { ok: errors.length === 0, errors, warnings };
}

/**
 * Snap a value to the nearest grid step (in mm). Default 0.5mm.
 */
export function snapToGrid(value, step = 0.5) {
  return Math.round(value / step) * step;
}
